// src/components/about/EngineeringApproach.styles.ts
import styled from 'styled-components';
import { motion } from 'framer-motion';

import { minDevice } from '../../styles/breakpoints';
export { SectionHeader, Eyebrow, EyebrowMark, Heading, Lede } from './SectionHeader.styles';

const sansStack = "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif";
const monoStack = "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";

const dotSize = '2.4rem';

/* ---------- Section shell ---------- */

export const ApproachRoot = styled.section`
  padding: ${({ theme }) => theme.spacing['3xl']} 0;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.text};
  border-top: 1px solid ${({ theme }) => theme.borderColor};
`;

export const ApproachContainer = styled.div`
  width: 90%;
  max-width: 1120px;
  margin: 0 auto;
`;

/* =========================================================
   DESKTOP — horizontal pipeline rail + single stage readout
   ========================================================= */

export const DesktopFlow = styled.div`
  display: none;

  @media ${minDevice.laptop} {
    display: block;
    margin-bottom: ${({ theme }) => theme.spacing.xl};
  }
`;

export const FlowList = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
`;

export const FlowNode = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.7rem;
  padding: 0 0.35rem;
  background: transparent;
  border: none;
  cursor: pointer;

  &:hover {
    transform: none;
    box-shadow: none;
  }

  /* The node dot carries the focus ring, so the button must not add a second
     one of its own. */
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  &:focus-visible .approach-node-dot {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.background},
      0 0 0 4px ${({ theme }) => theme.accent};
  }
`;

export const FlowConnector = styled(motion.span)`
  position: absolute;
  top: calc(${dotSize} / 2);
  left: -50%;
  width: 100%;
  height: 1px;
  background: ${({ theme }) => theme.borderColor};
  transform-origin: left center;
  pointer-events: none;
`;

export const NodeDot = styled(motion.span)<{ $active: boolean; $visited: boolean }>`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${dotSize};
  height: ${dotSize};
  flex: 0 0 ${dotSize};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-family: ${monoStack};
  font-size: 0.74rem;
  background: ${({ theme }) => theme.background};
  border: 1px solid
    ${({ $active, $visited, theme }) => ($active || $visited ? theme.accent : theme.borderColor)};
  color: ${({ $active, $visited, theme }) =>
    $active || $visited ? theme.accent : theme.colors.secondary};
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;
`;

export const NodeLabel = styled.span<{ $active: boolean }>`
  font-family: ${sansStack};
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.35;
  text-align: center;
  color: ${({ $active, theme }) => ($active ? theme.text : theme.colors.secondary)};
  opacity: ${({ $active }) => ($active ? 1 : 0.65)};
  transition: color 0.2s, opacity 0.2s;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/* ---------- Stage readout ---------- */

export const StagePanel = styled.div`
  display: none;

  @media ${minDevice.laptop} {
    display: flex;
    min-height: 21rem;
    padding: ${({ theme }) => theme.spacing.xl};
    background: ${({ theme }) => theme.cardBg};
    border: ${({ theme }) => theme.cardBorder};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
  }
`;

export const StageBody = styled(motion.div)`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

export const StageHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.85rem;
`;

export const StageIndex = styled.span`
  font-family: ${monoStack};
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  color: ${({ theme }) => theme.accent};
`;

export const StageTitle = styled.h3`
  margin: 0;
  font-family: ${sansStack};
  font-size: 1.35rem;
  line-height: 1.3;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: ${({ theme }) => theme.text};
`;

export const StageIntent = styled.p`
  margin: 0;
  font-family: ${sansStack};
  font-size: 1.02rem;
  line-height: 1.6;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
`;

export const StageDetail = styled.p`
  margin: 0;
  max-width: 68ch;
  font-family: ${sansStack};
  font-size: 0.95rem;
  line-height: 1.75;
  color: ${({ theme }) => theme.colors.secondary};
`;

export const StageSplit = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.borderColor};

  @media ${minDevice.desktop} {
    grid-template-columns: minmax(0, 4fr) minmax(0, 6fr);
    gap: 2.5rem;
  }
`;

export const StageCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  min-width: 0;
`;

export const MetaLabel = styled.span`
  font-family: ${monoStack};
  font-size: 0.7rem;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
  opacity: 0.7;
`;

export const PatternList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const Pattern = styled.li`
  font-family: ${monoStack};
  font-size: 0.73rem;
  padding: 0.3rem 0.55rem;
  color: ${({ theme }) => theme.colors.secondary};
  background: ${({ theme }) => theme.surface};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

export const EvidenceText = styled.p`
  margin: 0;
  font-family: ${sansStack};
  font-size: 0.9rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.secondary};
`;

/* ---------- Scope note ---------- */

export const ApproachNote = styled.p`
  margin: ${({ theme }) => theme.spacing.xl} 0 0;
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  font-family: ${sansStack};
  font-size: 0.88rem;
  line-height: 1.75;
  color: ${({ theme }) => theme.colors.secondary};

  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.text};
  }
`;

/* =========================================================
   MOBILE — vertical stepper / single-open accordion
   ========================================================= */

export const MobileStepper = styled.div`
  @media ${minDevice.laptop} {
    display: none;
  }
`;

export const StepList = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.cardBg};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
`;

export const StepItem = styled.div`
  border-top: 1px solid ${({ theme }) => theme.borderColor};

  &:first-child {
    border-top: none;
  }
`;

export const StepButton = styled.button<{ $active: boolean; $isLast: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.9rem;
  width: 100%;
  padding: 1.05rem 1.1rem;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;

  &:hover {
    transform: none;
    box-shadow: none;
  }

  ${({ $isLast, theme }) =>
    !$isLast &&
    `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 1.85rem;
      width: 1px;
      background: ${theme.borderColor};
      pointer-events: none;
    }
  `}

  &:focus-visible {
    outline: none;
    background: ${({ theme }) => theme.surface};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.borderColor};
  }
`;

export const StepIndex = styled.span<{ $active: boolean }>`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  flex: 0 0 1.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-family: ${monoStack};
  font-size: 0.62rem;
  background: ${({ theme }) => theme.background};
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.accent : theme.borderColor)};
  color: ${({ $active, theme }) => ($active ? theme.accent : theme.colors.secondary)};
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const StepText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

export const StepTitle = styled.span<{ $active: boolean }>`
  font-family: ${sansStack};
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.4;
  color: ${({ $active, theme }) => ($active ? theme.text : theme.colors.secondary)};
`;

export const StepHint = styled.span`
  font-family: ${sansStack};
  font-size: 0.82rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.secondary};
  opacity: 0.65;
`;

export const StepChevron = styled(motion.span)<{ $active: boolean }>`
  position: relative;
  z-index: 1;
  display: inline-flex;
  font-size: 0.65rem;
  color: ${({ $active, theme }) => ($active ? theme.accent : theme.colors.secondary)};
`;

export const StepRegion = styled(motion.div)`
  overflow: hidden;
`;

export const StepBody = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  padding: 0 1.1rem 1.4rem 3.5rem;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;